import { useState, useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  MessageCircle, Send, Paperclip, Mic, ArrowLeft, Search, Plus,
  Shield, FileText, Users, Siren, Clock, Check, CheckCheck,
  AlertTriangle, Phone, Video, PhoneMissed, PhoneOutgoing,
  PhoneIncoming, Stethoscope, Calendar, X, ChevronRight,
  CreditCard, Crown, Heart, Building2, Play, CheckCircle2,
  XCircle, AlertCircle, UserPlus, Info, Pill, Share2,
} from 'lucide-react';
import { patientApi } from '../api';
import { getPatientToken } from '../../../api/http';
import type {
  PatientConversation, PatientChatMessage, PatientCallLogEntry,
  PatientTelemedicineSession,
} from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import Spinner from '../../../components/ui/Spinner';

// Socket.IO needs the bare server origin, not the /api/patient-suffixed
// REST base — VITE_API_BASE_URL already holds that (see .env.example).
const SOCKET_URL = (import.meta.env?.VITE_API_BASE_URL as string | undefined) || 'http://localhost:4000';

type MainTab = 'chats' | 'calls' | 'groups' | 'telemedicine';

function fmtRelative(d?: string | null) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function fmtTime(d: string) {
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateTime(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(s?: number | null) {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

const TYPE_COLORS: Record<string, string> = {
  DOCTOR_CHAT: 'bg-teal-500',
  LAB_SUPPORT: 'bg-purple-500',
  PHARMACY_SUPPORT: 'bg-blue-500',
  HOSPITAL_SUPPORT: 'bg-cyan-500',
  ECC_EMERGENCY: 'bg-red-500',
  FAMILY_GROUP: 'bg-emerald-500',
  CARE_TEAM: 'bg-amber-500',
  GENERAL_GROUP: 'bg-gray-500',
};

const TYPE_LABELS: Record<string, string> = {
  DOCTOR_CHAT: 'Doctor',
  LAB_SUPPORT: 'Lab',
  PHARMACY_SUPPORT: 'Pharmacy',
  HOSPITAL_SUPPORT: 'Hospital',
  ECC_EMERGENCY: 'Emergency',
  FAMILY_GROUP: 'Family',
  CARE_TEAM: 'Care Team',
  GENERAL_GROUP: 'Group',
};

const GROUP_META: Record<string, { icon: typeof Users; color: string; bg: string }> = {
  FAMILY_GROUP: { icon: Heart, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  CARE_TEAM: { icon: Stethoscope, color: 'text-amber-600', bg: 'bg-amber-50' },
  GENERAL_GROUP: { icon: Users, color: 'text-gray-600', bg: 'bg-gray-100' },
  ECC_EMERGENCY: { icon: Siren, color: 'text-red-600', bg: 'bg-red-50' },
  HOSPITAL_SUPPORT: { icon: Building2, color: 'text-cyan-600', bg: 'bg-cyan-50' },
};

const TELE_STATUS: Record<string, { bg: string; text: string; icon: typeof CheckCircle2 }> = {
  SCHEDULED: { bg: 'bg-blue-50', text: 'text-blue-700', icon: Calendar },
  WAITING: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
  IN_PROGRESS: { bg: 'bg-green-50', text: 'text-green-700', icon: Play },
  COMPLETED: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle2 },
  CANCELLED: { bg: 'bg-gray-50', text: 'text-gray-500', icon: XCircle },
  NO_SHOW: { bg: 'bg-red-50', text: 'text-red-600', icon: AlertCircle },
};

function StatusIcon({ status }: { status: string }) {
  if (status === 'FAILED') return <span className="text-[9px] font-semibold text-red-500">Not sent</span>;
  if (status === 'READ') return <CheckCheck className="w-3 h-3 text-blue-500" />;
  if (status === 'DELIVERED') return <CheckCheck className="w-3 h-3 text-gray-400" />;
  return <Check className="w-3 h-3 text-gray-400" />;
}

export default function MyChat() {
  const [tab, setTab] = useState<MainTab>('chats');
  const [conversations, setConversations] = useState<PatientConversation[]>([]);
  const [selected, setSelected] = useState<PatientConversation | null>(null);
  const [messages, setMessages] = useState<PatientChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [nhcId, setNhcId] = useState('');
  const [search, setSearch] = useState('');
  const [showMobileList, setShowMobileList] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);

  const [calls, setCalls] = useState<PatientCallLogEntry[]>([]);
  const [groups, setGroups] = useState<PatientConversation[]>([]);
  const [teleSessions, setTeleSessions] = useState<PatientTelemedicineSession[]>([]);
  const [callFilter, setCallFilter] = useState<'all' | 'missed' | 'voice' | 'video'>('all');

  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newChatNhcId, setNewChatNhcId] = useState('');
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [newGroupType, setNewGroupType] = useState('FAMILY_GROUP');
  const [newGroupMembers, setNewGroupMembers] = useState('');
  const [showShareDoc, setShowShareDoc] = useState(false);
  const [shareConsent, setShareConsent] = useState(false);
  const [shareDuration, setShareDuration] = useState('24');
  const [shareDocType, setShareDocType] = useState<'certificate' | 'health_record' | 'prescription' | 'lab_report'>('certificate');

  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const selectedRef = useRef<PatientConversation | null>(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Each call fails independently — calls/groups hit endpoints that
    // aren't built yet and will 404, but that must never block the real,
    // working conversations/profile/telemedicine data from loading.
    Promise.all([
      patientApi.getConversations().catch(() => []),
      patientApi.getProfile().catch(() => null),
      patientApi.getCalls().catch(() => []),
      patientApi.getGroups().catch(() => []),
      patientApi.getTelemedicine().catch(() => []),
    ]).then(([convs, profile, callList, groupList, teleList]) => {
      if (!alive) return;
      setConversations(convs);
      if (profile) setNhcId(profile.nhcId);
      setCalls(callList);
      setGroups(groupList);
      setTeleSessions(teleList);
      // `?c=<conversationId>` — opened from a teleconsultation's "Message the
      // doctor": land on THAT conversation, the one the doctor sees too.
      const wanted = new URLSearchParams(window.location.search).get('c');
      const target = wanted ? convs.find((c) => c.id === wanted) : undefined;
      if (target) loadConversation(target);
      else if (convs.length > 0 && window.innerWidth >= 768) {
        loadConversation(convs[0]);
      }
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Live push for new messages — chat still works via REST/polling
  // (getConversation/sendMessage above) if the socket never connects.
  useEffect(() => {
    const token = getPatientToken();
    if (!token) return;

    const socket = io(SOCKET_URL, { auth: { token } });
    socketRef.current = socket;

    socket.on('message:new', (msg: PatientChatMessage) => {
      const current = selectedRef.current;
      if (current && msg.conversationId === current.id) {
        setMessages(prev => (prev.some(m => m.id === msg.id) ? prev : [...prev, msg]));
      }
      setConversations(prev => prev.map(c => c.id === msg.conversationId
        ? { ...c, lastMessagePreview: msg.content, lastMessageAt: msg.createdAt, unreadCount: current?.id === msg.conversationId ? 0 : c.unreadCount + 1 }
        : c));
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function loadConversation(conv: PatientConversation) {
    if (selected && selected.id !== conv.id) socketRef.current?.emit('conversation:leave', selected.id);
    socketRef.current?.emit('conversation:join', conv.id);
    setSelected(conv);
    setShowMobileList(false);
    setMsgLoading(true);
    setTab('chats');
    patientApi.getConversation(conv.id).then(full => {
      setMessages(full.messages || []);
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unreadCount: 0 } : c));
    }).catch(() => {}).finally(() => setMsgLoading(false));
  }

  async function handleSend() {
    if (!input.trim() || !selected) return;
    const text = input.trim();
    setInput('');
    const optimistic: PatientChatMessage = {
      id: `temp-${Date.now()}`,
      conversationId: selected.id,
      senderNhcId: nhcId,
      senderName: 'You',
      senderRole: 'PATIENT',
      messageType: 'TEXT',
      content: text,
      consentRequired: false,
      consentGranted: false,
      deliveryStatus: 'SENT',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    try {
      const msg = await patientApi.sendMessage(selected.id, { content: text });
      setMessages(prev => prev.map(m => m.id === optimistic.id ? msg : m));
      setConversations(prev => prev.map(c =>
        c.id === selected.id ? { ...c, lastMessagePreview: text, lastMessageAt: new Date().toISOString() } : c
      ));
    } catch {
      // A message the server never received must not sit in the thread with a
      // delivered tick — mark it FAILED so the patient knows to resend.
      setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...m, deliveryStatus: 'FAILED' } : m));
    }
  }

  async function handleNewChat() {
    if (!newChatNhcId.trim() || creating) return;
    setCreating(true);
    try {
      const conv = await patientApi.createConversation({
        conversationType: 'DOCTOR_CHAT',
        participantNhcIds: [newChatNhcId.trim()],
        title: `Chat with ${newChatNhcId.trim()}`,
      });
      setConversations(prev => [conv, ...prev]);
      loadConversation(conv);
      setShowNewChat(false);
      setNewChatNhcId('');
    } catch { /* handled */ } finally {
      setCreating(false);
    }
  }

  async function handleNewGroup() {
    if (!newGroupTitle.trim() || creating) return;
    setCreating(true);
    const nhcIds = newGroupMembers.split(',').map(s => s.trim()).filter(Boolean);
    try {
      const group = await patientApi.createGroup({
        title: newGroupTitle.trim(),
        conversationType: newGroupType,
        participantNhcIds: nhcIds,
      });
      setGroups(prev => [group, ...prev]);
      setShowNewGroup(false);
      setNewGroupTitle('');
      setNewGroupMembers('');
    } catch { /* handled */ } finally {
      setCreating(false);
    }
  }

  const filteredConvs = conversations.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.title?.toLowerCase().includes(s) || c.lastMessagePreview?.toLowerCase().includes(s);
  });

  const filteredCalls = calls.filter(c => {
    if (callFilter === 'missed') return c.status === 'MISSED';
    if (callFilter === 'voice') return c.callType === 'VOICE';
    if (callFilter === 'video') return c.callType === 'VIDEO';
    return true;
  });

  const totalUnread = conversations.reduce((s, c) => s + (c.unreadCount || 0), 0);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const TABS: { key: MainTab; label: string; icon: typeof MessageCircle; badge?: number }[] = [
    { key: 'chats', label: 'Chats', icon: MessageCircle, badge: totalUnread },
    { key: 'calls', label: 'Calls', icon: Phone, badge: calls.filter(c => c.status === 'MISSED').length },
    { key: 'groups', label: 'Groups', icon: Users },
    { key: 'telemedicine', label: 'Telemed', icon: Video },
  ];

  return (
    <div className="h-[calc(100vh-160px)] min-h-[500px] flex flex-col -mx-4 lg:-mx-6 -mt-4 lg:-mt-6">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-teal-600" />
          <h1 className="text-base font-bold text-gray-900">My Chat</h1>
          {nhcId && <NHCIdBadge nhcId={nhcId} size="sm" />}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setShowNewChat(true)} className="p-1.5 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-gray-100" title="New chat by NHC ID">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Consent banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 flex gap-2 text-[10px] text-amber-800 shrink-0">
        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
        <span>Messages are stored on your NeoHealthCard account. Document sharing requires your consent.</span>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-2 flex gap-0.5 overflow-x-auto shrink-0">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); if (t.key !== 'chats') { setSelected(null); setShowMobileList(true); } }}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {!!t.badge && t.badge > 0 && (
                <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{t.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden bg-gray-50">
        {tab === 'chats' && <ChatsView />}
        {tab === 'calls' && <CallsView />}
        {tab === 'groups' && <GroupsView />}
        {tab === 'telemedicine' && <TelemedicineView />}
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowNewChat(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Plus className="w-4 h-4 text-teal-600" /> Start New Chat</h3>
              <button onClick={() => setShowNewChat(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Participant NeoHealthCard ID</label>
              <input type="text" value={newChatNhcId} onChange={e => setNewChatNhcId(e.target.value)}
                placeholder="NHC-D-2026-MH-00101"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 font-mono"
                onKeyDown={e => { if (e.key === 'Enter') handleNewChat(); }}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={handleNewChat} disabled={creating} className="flex-1 px-4 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 disabled:opacity-60">{creating ? 'Starting…' : 'Start Chat'}</button>
              <button onClick={() => setShowNewChat(false)} className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* New Group Modal */}
      {showNewGroup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowNewGroup(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><UserPlus className="w-4 h-4 text-purple-600" /> Create Group</h3>
              <button onClick={() => setShowNewGroup(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Group Name</label>
              <input type="text" value={newGroupTitle} onChange={e => setNewGroupTitle(e.target.value)}
                placeholder="Family Health Group"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Type</label>
              <select value={newGroupType} onChange={e => setNewGroupType(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30">
                <option value="FAMILY_GROUP">Family Group</option>
                <option value="CARE_TEAM">Care Team</option>
                <option value="GENERAL_GROUP">General Group</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Member NHC IDs (comma-separated)</label>
              <input type="text" value={newGroupMembers} onChange={e => setNewGroupMembers(e.target.value)}
                placeholder="NHC-P-2026-MH-00501, NHC-D-2026-MH-00101"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30 font-mono"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={handleNewGroup} disabled={creating} className="flex-1 px-4 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-60">{creating ? 'Creating…' : 'Create'}</button>
              <button onClick={() => setShowNewGroup(false)} className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Document Modal */}
      {showShareDoc && selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowShareDoc(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Share2 className="w-4 h-4 text-teal-600" /> Share Health Document</h3>
              <button onClick={() => setShowShareDoc(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex gap-2 text-[10px] text-amber-800">
              <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p>Sharing requires your explicit consent. Only the selected document will be shared, not your full health history.</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Document Type</label>
              <select value={shareDocType} onChange={e => setShareDocType(e.target.value as any)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30">
                <option value="certificate">Health Certificate</option>
                <option value="health_record">Health Vault Record</option>
                <option value="prescription">Prescription</option>
                <option value="lab_report">Lab Report</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Access Duration</label>
              <select value={shareDuration} onChange={e => setShareDuration(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30">
                <option value="1">1 hour</option>
                <option value="6">6 hours</option>
                <option value="24">24 hours</option>
                <option value="72">3 days</option>
                <option value="168">7 days</option>
              </select>
            </div>
            <label className="flex items-start gap-2 p-2 rounded-lg bg-gray-50">
              <input type="checkbox" checked={shareConsent} onChange={e => setShareConsent(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
              <span className="text-[11px] text-gray-700">I consent to sharing the selected document with participants in "<strong>{selected.title}</strong>". The share is sent as a message in this conversation.</span>
            </label>
            <div className="flex gap-2">
              <button
                disabled={!shareConsent}
                onClick={async () => {
                  try {
                    // Persisted for real: DOCUMENT_SHARE message with a
                    // structured metadata object (the backend stores both).
                    const msg = await patientApi.sendMessage(selected.id, {
                      content: `Shared a ${shareDocType.replace(/_/g, ' ')} (consent granted)`,
                      messageType: 'DOCUMENT_SHARE',
                      consentGranted: true,
                      metadata: { docType: shareDocType, validHours: Number(shareDuration) },
                    });
                    setMessages(prev => [...prev, msg]);
                    setShowShareDoc(false);
                    setShareConsent(false);
                  } catch { alert('Failed to share document — nothing was sent. Please try again.'); }
                }}
                className="flex-1 px-4 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Shield className="w-3 h-3 inline mr-1" /> Share with Consent
              </button>
              <button onClick={() => setShowShareDoc(false)} className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {detailOpen && selected && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setDetailOpen(false)}>
          <div className="bg-black/30 absolute inset-0" />
          <div className="relative bg-white w-80 max-w-full h-full shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Conversation Details</h3>
              <button onClick={() => setDetailOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full ${TYPE_COLORS[selected.conversationType] || 'bg-gray-500'} text-white flex items-center justify-center text-xl font-bold mx-auto mb-2`}>
                  {selected.isGroup ? <Users className="w-7 h-7" /> : (selected.title?.[0] || 'C')}
                </div>
                <p className="text-sm font-bold text-gray-900">{selected.title}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{selected.conversationType.replace(/_/g, ' ')}</p>
              </div>

              {selected.conversationType === 'ECC_EMERGENCY' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800">
                  <div className="flex items-center gap-1.5 font-semibold mb-1"><Siren className="w-3.5 h-3.5" /> Emergency Chat</div>
                  <p>This conversation is linked to the Emergency Command Center. All messages are priority-logged.</p>
                </div>
              )}

              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">Participants ({selected.participantNhcIds.length})</p>
                <div className="space-y-1.5">
                  {selected.participantNhcIds.map(id => {
                    const names = selected.participantNames as Record<string, string> | null;
                    return (
                      <div key={id} className="flex items-center gap-2 text-xs">
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600">
                          {(names?.[id] || id)[0]}
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-900 font-medium truncate">{names?.[id] || 'Unknown'}</p>
                          <p className="text-[10px] text-gray-400 font-mono">{id}</p>
                        </div>
                        {id === selected.groupAdminNhcId && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Privacy</p>
                <div className="text-[11px] text-gray-600 space-y-1">
                  <p className="flex items-center gap-1"><Shield className="w-3 h-3 text-emerald-500" /> Messages are stored on your NeoHealthCard account</p>
                  <p className="flex items-center gap-1"><FileText className="w-3 h-3 text-blue-500" /> Document sharing requires consent</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function ChatsView() {
    return (
      <div className="flex flex-1 overflow-hidden">
        {/* Conversation List */}
        <div className={`${showMobileList ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-72 lg:w-80 border-r border-gray-200 bg-white shrink-0`}>
          <div className="p-2.5 border-b border-gray-100">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredConvs.map(conv => {
              const color = TYPE_COLORS[conv.conversationType] || 'bg-gray-500';
              const isActive = selected?.id === conv.id;
              const initial = conv.title?.[0] || 'C';
              return (
                <button key={conv.id} onClick={() => loadConversation(conv)}
                  className={`w-full px-3 py-2.5 flex items-center gap-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors text-left ${isActive ? 'bg-teal-50' : ''}`}>
                  <div className={`w-9 h-9 rounded-full ${color} text-white flex items-center justify-center text-xs font-bold shrink-0`}>
                    {conv.isGroup ? <Users className="w-3.5 h-3.5" /> : initial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-xs font-semibold text-gray-900 truncate">{conv.title}</p>
                      <span className="text-[9px] text-gray-400 shrink-0">{fmtRelative(conv.lastMessageAt)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-[11px] text-gray-500 truncate">{conv.lastMessagePreview}</p>
                      {conv.unreadCount > 0 && (
                        <span className="w-4 h-4 rounded-full bg-teal-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0">{conv.unreadCount}</span>
                      )}
                    </div>
                    <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded mt-0.5 inline-block ${TYPE_COLORS[conv.conversationType]?.replace('bg-', 'bg-opacity-10 text-') || 'bg-gray-100 text-gray-500'}`}
                      style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                      {TYPE_LABELS[conv.conversationType] || conv.conversationType.replace(/_/g, ' ')}
                    </span>
                  </div>
                </button>
              );
            })}
            {filteredConvs.length === 0 && (
              <div className="p-6 text-center text-gray-400 text-xs">No conversations</div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${!showMobileList ? 'flex' : 'hidden'} md:flex flex-col flex-1 min-w-0`}>
          {selected ? (
            <>
              {/* Chat Header */}
              <div className="px-3 py-2.5 border-b border-gray-200 bg-white flex items-center gap-2.5 shrink-0">
                <button onClick={() => { setShowMobileList(true); setSelected(null); }} className="md:hidden p-1 text-gray-500 hover:text-gray-700">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className={`w-8 h-8 rounded-full ${TYPE_COLORS[selected.conversationType] || 'bg-gray-500'} text-white flex items-center justify-center text-xs font-bold shrink-0`}>
                  {selected.isGroup ? <Users className="w-3.5 h-3.5" /> : (selected.title?.[0] || 'C')}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{selected.title}</p>
                  <p className="text-[10px] text-gray-500">
                    {TYPE_LABELS[selected.conversationType] || selected.conversationType.replace(/_/g, ' ')}
                    {selected.isGroup && ` · ${selected.participantNhcIds.length} members`}
                    {selected.conversationType === 'ECC_EMERGENCY' && ' · Emergency Linked'}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={() => setDetailOpen(true)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" title="Details">
                    <Info className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* ECC Banner */}
              {selected.conversationType === 'ECC_EMERGENCY' && (
                <div className="bg-red-50 border-b border-red-200 px-3 py-1.5 flex items-center gap-1.5 text-[10px] text-red-700 shrink-0">
                  <Siren className="w-3 h-3" />
                  <span className="font-semibold">Emergency Channel</span> — In a real emergency always call 112/108
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-gray-50/50">
                {msgLoading ? (
                  <div className="flex justify-center py-10"><Spinner /></div>
                ) : (
                  <>
                    {messages.map(msg => {
                      const isMe = msg.senderNhcId === nhcId;
                      const isMedReminder = msg.messageType === 'MEDICINE_REMINDER';
                      const isSystem = !isMedReminder && (msg.senderRole === 'SYSTEM' || msg.messageType === 'SYSTEM');
                      const isDocShare = ['DOCUMENT_SHARE', 'CERTIFICATE_SHARE', 'PRESCRIPTION_SHARE', 'LAB_REPORT_SHARE'].includes(msg.messageType);

                      if (isSystem) {
                        return (
                          <div key={msg.id} className="flex justify-center">
                            <span className="text-[10px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{msg.content}</span>
                          </div>
                        );
                      }

                      if (isMedReminder) {
                        const meta = typeof msg.metadata === 'string' ? JSON.parse(msg.metadata || '{}') : (msg.metadata || {});
                        const status = meta.status || 'PENDING';
                        return (
                          <div key={msg.id} className="flex justify-start">
                            <div className="max-w-[85%] w-full sm:max-w-[75%]">
                              <p className="text-[10px] text-gray-500 mb-0.5 ml-1">{msg.senderName}</p>
                              <div className="bg-white rounded-2xl rounded-bl-md shadow-sm border border-emerald-100 overflow-hidden">
                                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border-b border-emerald-100">
                                  <Pill className="w-4 h-4 text-emerald-600" />
                                  <span className="text-xs font-semibold text-emerald-800">Medicine Reminder</span>
                                  <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                    status === 'TAKEN' ? 'bg-green-100 text-green-700' :
                                    status === 'SKIPPED' ? 'bg-gray-100 text-gray-600' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>{status}</span>
                                </div>
                                <div className="px-3 py-2">
                                  <p className="text-sm font-medium text-gray-900">{meta.medicineName || msg.content}</p>
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-gray-500">
                                    {meta.dosage && <span>{meta.dosage}</span>}
                                    {meta.timing && <span>⏰ {meta.timing}</span>}
                                    {meta.instructions && <span className="text-amber-600">{meta.instructions}</span>}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1.5">{msg.content}</p>
                                </div>
                                {status === 'PENDING' && (
                                  <div className="flex border-t border-emerald-100">
                                    <button
                                      onClick={async () => {
                                        if (!meta.reminderId) return;
                                        try {
                                          await patientApi.logAdherence(meta.reminderId, { taken: true, source: 'CHAT' });
                                          // Reflect the logged state so the card doesn't invite double-logging.
                                          setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, metadata: { ...meta, status: 'TAKEN' } } : m));
                                        } catch { alert('Could not log the dose — server unreachable. Please try again.'); }
                                      }}
                                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-green-700 hover:bg-green-50 transition-colors border-r border-emerald-100"
                                    >
                                      <Check className="w-3.5 h-3.5" /> Taken
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (!meta.reminderId) return;
                                        try {
                                          await patientApi.logAdherence(meta.reminderId, { taken: false, source: 'CHAT' });
                                          setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, metadata: { ...meta, status: 'SKIPPED' } } : m));
                                        } catch { alert('Could not log the dose — server unreachable. Please try again.'); }
                                      }}
                                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                                    >
                                      <X className="w-3.5 h-3.5" /> Skip
                                    </button>
                                  </div>
                                )}
                                <div className="px-3 py-1 bg-gray-50 border-t border-gray-100">
                                  <span className="text-[9px] text-gray-400">{fmtTime(msg.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%]`}>
                            {!isMe && <p className="text-[10px] text-gray-500 mb-0.5 ml-1">{msg.senderName}</p>}
                            <div className={`rounded-2xl px-3 py-2 ${isMe ? 'bg-teal-600 text-white rounded-br-md' : 'bg-white text-gray-900 rounded-bl-md shadow-sm border border-gray-100'}`}>
                              {isDocShare && (
                                <div className={`flex items-center gap-1.5 text-[10px] mb-1 pb-1 border-b ${isMe ? 'border-teal-500 text-teal-100' : 'border-gray-100 text-gray-500'}`}>
                                  <FileText className="w-3 h-3" />
                                  <span>{msg.messageType.replace(/_/g, ' ')}</span>
                                  {msg.consentGranted && <Shield className="w-3 h-3 text-emerald-400" />}
                                </div>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <div className={`flex items-center gap-1 mt-0.5 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <span className={`text-[9px] ${isMe ? 'text-teal-200' : 'text-gray-400'}`}>{fmtTime(msg.createdAt)}</span>
                                {isMe && <StatusIcon status={msg.deliveryStatus} />}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </>
                )}
              </div>

              {/* Composer */}
              <div className="px-3 py-2.5 border-t border-gray-200 bg-white shrink-0">
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setShowShareDoc(true)} className="p-1.5 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-teal-50" title="Share health record / document">
                    <Share2 className="w-4 h-4" />
                  </button>
                  <input type="text" value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Type a message..."
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                  />
                  <button onClick={handleSend} disabled={!input.trim()}
                    className="p-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-[9px] text-gray-400 text-center mt-1.5">Secured by NeoHealthCard · Messages are stored on your account</p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-6 bg-white">
              <div>
                <MessageCircle className="w-14 h-14 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 text-sm font-medium">Select a conversation</p>
                <p className="text-gray-400 text-xs mt-1">Choose from your NeoHealthCard contacts</p>
                <button onClick={() => setShowNewChat(true)}
                  className="mt-4 px-4 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 inline-flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Start Chat by NHC ID
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function CallsView() {
    const callFilters: { key: typeof callFilter; label: string }[] = [
      { key: 'all', label: `All (${calls.length})` },
      { key: 'missed', label: `Missed (${calls.filter(c => c.status === 'MISSED').length})` },
      { key: 'voice', label: `Voice (${calls.filter(c => c.callType === 'VOICE').length})` },
      { key: 'video', label: `Video (${calls.filter(c => c.callType === 'VIDEO').length})` },
    ];

    return (
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {callFilters.map(f => (
            <button key={f.key} onClick={() => setCallFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                callFilter === f.key ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {filteredCalls.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Phone className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No calls found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredCalls.map(call => {
              const isMissed = call.status === 'MISSED';
              const isVideo = call.callType === 'VIDEO';
              const isOut = call.direction === 'OUTGOING';
              const otherName = isOut ? call.calleeName : call.callerName;
              const otherNhc = isOut ? call.calleeNhcId : call.callerNhcId;

              return (
                <div key={call.id} className={`bg-white rounded-xl p-3 flex items-center gap-3 border ${isMissed ? 'border-l-4 border-l-red-300' : 'border-gray-100'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isMissed ? 'bg-red-50' : isVideo ? 'bg-blue-50' : 'bg-green-50'}`}>
                    {isMissed ? <PhoneMissed className="w-4 h-4 text-red-500" /> : isVideo ? <Video className="w-4 h-4 text-blue-500" /> : <Phone className="w-4 h-4 text-green-500" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-semibold ${isMissed ? 'text-red-600' : 'text-gray-900'}`}>{otherName}</p>
                      {call.isTelemedicine && <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">TELEMED</span>}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mt-0.5">
                      {isOut ? <PhoneOutgoing className="w-3 h-3" /> : <PhoneIncoming className="w-3 h-3" />}
                      <span>{isOut ? 'Out' : 'In'} · {isVideo ? 'Video' : 'Voice'}</span>
                      {!isMissed && call.durationSeconds && <span>· {fmtDuration(call.durationSeconds)}</span>}
                    </div>
                    <p className="text-[9px] text-gray-400 font-mono mt-0.5">{otherNhc}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[9px] text-gray-400">{fmtRelative(call.startedAt)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[9px] text-gray-400 text-center">Voice/video calling is not available yet</p>
      </div>
    );
  }

  function GroupsView() {
    return (
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="flex justify-end">
          <button onClick={() => setShowNewGroup(true)}
            className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> New Group
          </button>
        </div>

        {groups.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Users className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No groups yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {groups.map(group => {
              const meta = GROUP_META[group.conversationType] || GROUP_META.GENERAL_GROUP;
              const GIcon = meta.icon;
              const names = group.participantNames as Record<string, string> | null;

              return (
                <div key={group.id} className="bg-white rounded-xl p-3 border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl ${meta.bg} flex items-center justify-center shrink-0`}>
                      <GIcon className={`w-5 h-5 ${meta.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-xs font-semibold text-gray-900">{group.title}</p>
                        {group.unreadCount > 0 && (
                          <span className="w-4 h-4 rounded-full bg-purple-500 text-white text-[9px] font-bold flex items-center justify-center">{group.unreadCount}</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">{group.lastMessagePreview}</p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400">
                        <span>{group.participantNhcIds.length} members</span>
                        <span>·</span>
                        <span>{fmtRelative(group.lastMessageAt)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {group.participantNhcIds.slice(0, 4).map(id => (
                          <span key={id} className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-100 flex items-center gap-0.5">
                            {id === group.groupAdminNhcId && <Crown className="w-2.5 h-2.5 text-amber-500" />}
                            {names?.[id] || id.slice(-7)}
                          </span>
                        ))}
                        {group.participantNhcIds.length > 4 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-400">+{group.participantNhcIds.length - 4}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-2.5 pt-2 border-t border-gray-100">
                    <button onClick={() => loadConversation(group)}
                      className="flex-1 px-2.5 py-1.5 bg-gray-50 text-gray-700 text-[11px] font-medium rounded-lg hover:bg-gray-100 flex items-center justify-center gap-1">
                      <MessageCircle className="w-3 h-3" /> Chat
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[9px] text-gray-400 text-center">Group calling is not available yet</p>
      </div>
    );
  }

  function TelemedicineView() {
    const upcoming = teleSessions.filter(s => s.status === 'SCHEDULED' || s.status === 'WAITING' || s.status === 'IN_PROGRESS');
    const completed = teleSessions.filter(s => s.status === 'COMPLETED' || s.status === 'ENDED');
    const [teleTab, setTeleTab] = useState<'upcoming' | 'completed' | 'all'>('upcoming');
    const displayed = teleTab === 'upcoming' ? upcoming : teleTab === 'completed' ? completed : teleSessions;

    return (
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <Calendar className="w-4 h-4 text-blue-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{upcoming.length}</p>
            <p className="text-[9px] text-gray-500">Upcoming</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{completed.length}</p>
            <p className="text-[9px] text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <Clock className="w-4 h-4 text-teal-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">
              {completed.reduce((s, c) => s + (c.durationSeconds || 0), 0) > 0
                ? `${Math.round(completed.reduce((s, c) => s + (c.durationSeconds || 0), 0) / 60)}m`
                : '—'}
            </p>
            <p className="text-[9px] text-gray-500">Total Time</p>
          </div>
        </div>

        <div className="flex gap-2">
          {([
            { key: 'upcoming' as const, label: `Upcoming (${upcoming.length})` },
            { key: 'completed' as const, label: `Completed (${completed.length})` },
            { key: 'all' as const, label: `All (${teleSessions.length})` },
          ]).map(t => (
            <button key={t.key} onClick={() => setTeleTab(t.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                teleTab === t.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {displayed.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Video className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No sessions</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayed.map(session => {
              const st = TELE_STATUS[session.status] || TELE_STATUS.SCHEDULED;
              const StIcon = st.icon;
              const isUpcoming = session.status === 'SCHEDULED' || session.status === 'WAITING';

              return (
                <div key={session.id} className={`bg-white rounded-xl p-3 border ${isUpcoming ? 'border-l-4 border-l-blue-400 border-gray-100' : 'border-gray-100'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Stethoscope className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                        <p className="text-xs font-semibold text-gray-900">{session.doctorName}</p>
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${st.bg} ${st.text} flex items-center gap-0.5`}>
                          <StIcon className="w-2.5 h-2.5" /> {session.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[10px] text-gray-500">
                        {session.hospitalName && <span>{session.hospitalName}</span>}
                        {session.speciality && <span>· {session.speciality}</span>}
                      </div>
                      <div className="flex flex-wrap gap-2 text-[10px] text-gray-500 mt-1">
                        <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" /> {fmtDateTime(session.scheduledAt)}</span>
                        {session.durationSeconds && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {fmtDuration(session.durationSeconds)}</span>}
                      </div>
                      {session.doctorNhcId && <p className="text-[9px] text-gray-400 mt-0.5 font-mono">Doctor: {session.doctorNhcId}</p>}
                      {session.appointmentId && <p className="text-[9px] text-gray-400 font-mono">Appointment: {session.appointmentId.slice(0, 8)}...</p>}
                    </div>
                    {/* Enter the SAME consultation the doctor opens. */}
                    {session.appointmentId && ['SCHEDULED', 'WAITING', 'IN_PROGRESS'].includes(session.status) && (
                      <a href={`/patient/telemedicine/consult/${encodeURIComponent(session.appointmentId)}`}
                        className="shrink-0 text-[10px] font-semibold px-2.5 py-1.5 rounded-lg bg-teal-600 text-white">
                        {session.status === 'IN_PROGRESS' ? 'Join call' : 'Open'}
                      </a>
                    )}
                  </div>

                  {isUpcoming && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-[9px] text-gray-500 mb-1.5 font-medium">Share records for this session (creates a real consent grant):</p>
                      <div className="flex flex-wrap gap-1.5">
                        {([
                          // The scopes the server actually gates on. These
                          // were `health_summary` / `prescriptions` /
                          // `lab_reports`, a vocabulary nothing read.
                          { label: 'Medical history', scope: 'MEDICAL_HISTORY' },
                          { label: 'Prescriptions', scope: 'PRESCRIPTIONS' },
                          { label: 'Lab Reports', scope: 'LAB_REPORTS' },
                        ]).map(opt => (
                          <button key={opt.scope} onClick={async () => {
                            if (!confirm(`Share your ${opt.label.toLowerCase()} with ${session.doctorName}? A 24-hour consent grant will be recorded on your account — you can revoke it anytime from Consent & Sharing.`)) return;
                            if (!session.doctorNhcId) {
                              // Binds by card id; without one nothing is shared.
                              alert('This doctor has no NeoHealthCard id on this session, so records cannot be shared with them.');
                              return;
                            }
                            try {
                              await patientApi.createConsent({
                                sharedWithName: session.doctorName || 'Doctor',
                                sharedWithRole: 'DOCTOR',
                                sharedWithNhcId: session.doctorNhcId,
                                scopes: [opt.scope],
                                validHours: 24,
                              });
                              alert(`Consent recorded — ${session.doctorName} is allowed to view your ${opt.label.toLowerCase()} for 24 hours. Manage it in Consent & Sharing.`);
                            } catch {
                              alert('Consent could NOT be recorded — the server is unreachable. Nothing was shared.');
                            }
                          }} className="text-[9px] px-2 py-1 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 flex items-center gap-1 font-medium">
                            <Share2 className="w-2.5 h-2.5" /> {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {session.status === 'COMPLETED' && session.summary && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-[10px] font-semibold text-gray-500 mb-0.5">Summary</p>
                      <p className="text-[11px] text-gray-700 leading-relaxed">{session.summary}</p>
                    </div>
                  )}

                  <div className="mt-2 pt-1.5 border-t border-gray-100 flex flex-wrap gap-1.5">
                    {session.healthHistoryConsent && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 flex items-center gap-0.5">
                        <Shield className="w-2.5 h-2.5" /> History shared
                      </span>
                    )}
                    {session.recordingConsent && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-600 flex items-center gap-0.5">
                        <Shield className="w-2.5 h-2.5" /> Recording consent
                      </span>
                    )}
                    {session.prescriptionId && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 flex items-center gap-0.5">
                        <FileText className="w-2.5 h-2.5" /> Rx
                      </span>
                    )}
                    {session.followUpId && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 flex items-center gap-0.5">
                        <Calendar className="w-2.5 h-2.5" /> Follow-up
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[9px] text-gray-400 text-center">Video consultations are joined via the link your clinic shares — in-app video is not available yet</p>
      </div>
    );
  }

}
